import React, { useState, useEffect, useContext } from 'react';
import { Link } from "react-router-dom";
import 'bulma/css/bulma.min.css';

import { ScanTypeSlug, ScanType } from '../scanTypes.ts';
import { BeamlineContext, BeamlineLoadingState } from '../beamlineProvider.tsx';
import { LoadingBanner, LoadingState } from '../components/loadingBanner.tsx';
import { Coordinates, SortableTable } from '../components/sortableTable.tsx';
import { truthyJoin, timestampToTodayString, sortWithNumberParsing } from '../utils.tsx';
import AddScanType from './addScanType.tsx';
import './scanTypeTable.css';


// It's a magic number I know, and that's annoying.
// This is the number of columns to display in the table.
const FIXED_COLUMN_COUNT = 7;


// An elaboration of the different columns we can expect in the ScanType table.
enum ScanTypeField {
  Slug, Name, Description, DerivedFrom, Deprecated, LastModified, Parameters
};
const scanTypeFieldDisplayOrder: ScanTypeField[] = [
  ScanTypeField.Slug, ScanTypeField.Name, ScanTypeField.Description, ScanTypeField.DerivedFrom,
  ScanTypeField.Deprecated, ScanTypeField.LastModified, ScanTypeField.Parameters
];
const columnNames = [
  "Id", "Name", "Description", "Derived From", "Deprecated", "Last Modified", "Parameters"
];


const ScanTypeTable: React.FC = () => {

  const beamlineContext = useContext(BeamlineContext);
  const [loading, setLoading] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("");

  const [sortedScanTypeSlugs, setSortedScanTypeSlugs] = useState<ScanTypeSlug[]>([]);
  const [selectedScanTypeSlugs, setSelectedScanTypeSlugs] = useState<ScanTypeSlug[]>([]);

  const [tableSortColumn, setTableSortColumn] = useState<ScanTypeField>(ScanTypeField.Name);
  const [tableSortReverse, setTableSortReverse] = useState<boolean>(false);
  

  useEffect(() => {
    if (beamlineContext.loadingState == BeamlineLoadingState.Failed) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Failed to load Beamline. Are you sure you're still logged in?");
      return;
    } 
    if (beamlineContext.loadingState == BeamlineLoadingState.Succeeded) {
      setSortedScanTypeSlugs(beamlineContext.scanTypes.typeSlugsInDisplayOrder);
      setLoading(LoadingState.Success);
      return;
    }
    setLoading(LoadingState.Loading);
    setLoadingMessage("Loading Beamline...");
  }, [beamlineContext.changeCounter, beamlineContext.loadingState]);


  function sortScanTypeSlugs(field: ScanTypeField, reverse: boolean): ScanTypeSlug[] {
    const scanTypes = Array.from(beamlineContext.scanTypes.typesBySlug.values());
    var sorted:ScanType[] = [];
    switch (field) {
      case ScanTypeField.Slug:
        sorted = scanTypes.sort((a, b) => { return sortWithNumberParsing(a.slug, b.slug, reverse)});
        break;
      case ScanTypeField.Name:
        sorted = scanTypes.sort((a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)});
        break;
      case ScanTypeField.Description:
        sorted = scanTypes.sort((a, b) => { return sortWithNumberParsing(a.description, b.description, reverse)});
        break;
      case ScanTypeField.DerivedFrom:
        sorted = scanTypes.sort((a, b) => {
          const aName = a.slugDerivedFrom ? (beamlineContext.scanTypes.typesBySlug.get(a.slugDerivedFrom)?.name || "") : "";
          const bName = b.slugDerivedFrom ? (beamlineContext.scanTypes.typesBySlug.get(b.slugDerivedFrom)?.name || "") : "";
          return sortWithNumberParsing(aName, bName, reverse)
        });
        break;
      case ScanTypeField.Deprecated:
        sorted = scanTypes.sort(
            (a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)}
          ).sort(
            (a, b) => { return a.deprecated === b.deprecated ? 0 : (reverse ? b.deprecated : a.deprecated) ? -1 : 1 }
          );
        break;
      case ScanTypeField.LastModified:
        sorted = scanTypes.sort((a, b) => {
          const aDate = Date.parse(a.dateLastModified);
          const bDate = Date.parse(b.dateLastModified);
          return reverse ? bDate - aDate : aDate - bDate;
        });
        break;
      case ScanTypeField.Parameters:
        sorted = scanTypes.sort((a, b) => {
          const aCount = a.parameterIds.length;
          const bCount = b.parameterIds.length;
          return reverse ? bCount - aCount : aCount - bCount;
        });
        break;
    }
    return sorted.map((s) => s.slug);
  }


  function clickedTableSortHeader(f: ScanTypeField) {
    const sortDirection = (f == tableSortColumn) ? !tableSortReverse : tableSortReverse;
    setTableSortColumn(f);
    setTableSortReverse(sortDirection);
    setSortedScanTypeSlugs(sortScanTypeSlugs(f, sortDirection));
  }


  function tableRowSelectionChanged(identifiers: string[]) {
    setSelectedScanTypeSlugs(identifiers as unknown as ScanTypeSlug[]);
  }


  function renderHeaderCell(x: number): React.JSX.Element {
    const sortDirectionStyle = tableSortReverse ? "sortedup" : "sorteddown";
    const fieldType = scanTypeFieldDisplayOrder[x];
    const fieldName = columnNames[x];

    return (<th key={fieldName}
            className={truthyJoin("sortable", (tableSortColumn == fieldType) && sortDirectionStyle)}
            onClick={ () => clickedTableSortHeader(fieldType) }
            scope="col">{fieldName}</th>);
  }


  // Function provided to the EditableTable.  Given coordinates, renders an editable cell.
  // The editable cell is given the activationStatus and cellFunctions prepared by the EditableTable.
  function renderTableCell(coordinates: Coordinates): React.JSX.Element {
    const thisScanType = beamlineContext.scanTypes.typesBySlug.get(sortedScanTypeSlugs[coordinates.y]);
    if (!thisScanType) {
      return (<></>);
    }

    // Set the value for the cell, making any conversions needed, and apply additional per-column settings.
    switch (scanTypeFieldDisplayOrder[coordinates.x]) {
      case ScanTypeField.Slug:
        return (<>{thisScanType.slug}</>);
      case ScanTypeField.Name:
        return (<><Link to={ "scantype/" + thisScanType.slug }>{ thisScanType.name }</Link></>);
      case ScanTypeField.Description:
        return (<>{thisScanType.description ?? ""}</>);
      case ScanTypeField.DerivedFrom:
        return (<>{thisScanType.slugDerivedFrom ? (beamlineContext.scanTypes.typesBySlug.get(thisScanType.slugDerivedFrom)?.name || "") : ""}</>);
      case ScanTypeField.Deprecated:
        return (<>{thisScanType.deprecated ? "Yes" : ""}</>);
      case ScanTypeField.LastModified:
        return (<>{timestampToTodayString(Date.parse(thisScanType.dateLastModified) / 1000)}</>);
      case ScanTypeField.Parameters:
        return (<>{thisScanType.parameterIds.length}</>);
      default:
        return (<></>);
      }
  }


  const sortableTableFunctions = {
    renderHeaderCell: renderHeaderCell,
    renderTableCell: renderTableCell,
    rowSelectionChanged: tableRowSelectionChanged
  };


  // If we're in any loading state other than success,
  // display a loading banner instead of the content.
  if (loading != LoadingState.Success) {
    return (<LoadingBanner state={loading} message={loadingMessage}></LoadingBanner>)
  }

  return (
    <>
      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5"><strong>{ sortedScanTypeSlugs.length }</strong> Scan Types defined for { beamlineContext.beamline!.name }.</p>
          </div>
          <div className="level-item">
            <AddScanType />
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
          </div>
        </div>
      </nav>

      <div className="block" style={{ overflowX: "auto" }}>
        { sortedScanTypeSlugs.length == 0 ? (
          <p>( Use the Add button to add Scan Types. )</p>
        ) : (
          <SortableTable
              extraClasses="scantypetable"
              rowIdentifiers={ sortedScanTypeSlugs as unknown as string[] }
              columnCount={ FIXED_COLUMN_COUNT }
              displayRowSelectionBoxes={ true }
              tableFunctions={ sortableTableFunctions }
            />
        )}
      </div>
    </>
  )
}

export default ScanTypeTable
